import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cardService,
  type CardDTO,
  type CardQR,
  type CardShare,
  type WalletItem,
} from "../services/api/cardService";

export interface UseHealthPassCardReturn {
  /** Card data (holder, card number, validity) */
  cardData: CardDTO | null;
  /** Wallet items with counts and availability */
  walletItems: WalletItem[];
  /** The card's current QR codes; loaded with the card, never rotated by viewing */
  qrData: CardQR | null;
  /** Share links */
  shares: CardShare[];
  isLoading: boolean;
  isQrLoading: boolean;
  isSharesLoading: boolean;
  /** Issue a new code; the previous one stops working */
  generateQR: () => void;
  /** Create share link */
  createShare: (
    expiresIn: "24h" | "72h" | "7d",
    password?: string,
  ) => Promise<CardShare>;
  /** Revoke share link */
  revokeShare: (id: string) => Promise<void>;
}

export function useHealthPassCard(): UseHealthPassCardReturn {
  const queryClient = useQueryClient();

  const { data: cardData, isLoading: cardLoading } = useQuery({
    queryKey: ["healthpass-card"],
    queryFn: () => cardService.getCard(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  const { data: walletItems, isLoading: walletLoading } = useQuery({
    queryKey: ["healthpass-wallet"],
    queryFn: () => cardService.getWallet(),
    staleTime: 2 * 60 * 1000,
    retry: 2,
  });

  const qrQuery = useQuery({
    queryKey: ["healthpass-qr"],
    queryFn: () => cardService.getQR(),
    enabled: !!cardData,
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });

  // A new code only on request: the old one stops working.
  const rotateQR = useMutation({
    mutationFn: () => cardService.generateQR(),
    onSuccess: (data) => {
      queryClient.setQueryData(["healthpass-qr"], data);
    },
  });

  const { data: shares, isLoading: isSharesLoading } = useQuery({
    queryKey: ["healthpass-shares"],
    queryFn: () => cardService.getShares(),
    staleTime: 60 * 1000,
    retry: 1,
  });

  const createShareMutation = useMutation({
    mutationFn: ({
      expiresIn,
      password,
    }: {
      expiresIn: "24h" | "72h" | "7d";
      password?: string;
    }) => cardService.createShare(expiresIn, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["healthpass-shares"] });
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: (id: string) => cardService.revokeShare(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["healthpass-shares"] });
    },
  });

  return {
    cardData: cardData ?? null,
    walletItems: walletItems ?? [],
    qrData: qrQuery.data ?? null,
    shares: shares ?? [],
    isLoading: cardLoading || walletLoading,
    isQrLoading: qrQuery.isLoading || rotateQR.isPending,
    isSharesLoading,
    generateQR: () => rotateQR.mutate(),
    createShare: (expiresIn, password) =>
      createShareMutation.mutateAsync({ expiresIn, password }),
    revokeShare: (id) => revokeShareMutation.mutateAsync(id),
  };
}
